
import { animate, style, transition, trigger } from '@angular/animations';
import {
  ChangeDetectionStrategy, Component, EventEmitter
  , Input, Output, TemplateRef, OnChanges, OnInit, SimpleChanges
} from '@angular/core';
import { ColorHelper, formatLabel } from '@swimlane/ngx-charts';
import { id } from '@swimlane/ngx-charts';

@Component({
  selector: 'g[np-ngx-charts-circle-series-white]',
  template: `
      <svg:g ngx-charts-circle *ngFor="let circle of circles"
        class="circle"
        [cx]="circle.cx"
        [cy]="circle.cy"
        [r]="6"
        [fill]="circle.color"
        [class.active]="true"
        [pointerEvents]="circle.value === 0 ? 'none': 'all'"
        [data]="circle.value"
        [classNames]="circle.classNames"
        (select)="onClick($event, circle.label)"
        (mouseover)="onMouseover($event, circle.radius)"
        (mouseout)="onMouseout($event, circle.radius)"
        ngx-tooltip
        [tooltipDisabled]="tooltipDisabled"
        [tooltipPlacement]="'top'"
        [tooltipType]="'tooltip'"
        [tooltipTitle]="tooltipTemplate ? undefined : getTooltipText(circle)"
        [tooltipTemplate]="tooltipTemplate"
        [tooltipContext]="circle.data"
      />
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('animationState', [
      transition(':enter', [
        style({
          opacity: 1,
        }),
        animate(250, style({ opacity: 1 }))
      ])
    ])
  ]
})
export class CustomCircleSeriesWhiteComponent implements OnChanges, OnInit {

  @Input() data;
  @Input() type = 'standard';
  @Input() xScale;
  @Input() yScale;
  @Input() colors: ColorHelper;
  @Input() scaleType;
  @Input() visibleValue;
  @Input() activeEntries: any[];
  @Input() tooltipDisabled: false;
  @Input() tooltipTemplate: TemplateRef<any>;

  @Output() select = new EventEmitter();
  @Output() activate = new EventEmitter();
  @Output() deactivate = new EventEmitter();

  areaPath: any;
  circles: any; // active circle
  barVisible = true;
  gradientId: string;
  gradientFill: string;

  ngOnInit() {
    this.gradientId = 'grad' + id().toString();
    this.gradientFill = `url(#${this.gradientId})`;
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.update();
  }

  update(): void {
    this.circles = this.data.series.map((item, i) => {
      return this.mapDataPointToCircle(item, i);
    });
  }

  mapDataPointToCircle(d: any, i: number): any {
    const value = d.value;
    const label = d.name;
    const hideFirstLast = d.hideFirstLast;
    const tooltipLabel = formatLabel(label);
    const cy = this.yScale(this.type === 'standard' ? value : d.d1);
    const radius = hideFirstLast ? 0 : 3;
    const height = this.yScale.range()[0] - cy;
    const opacity = 0.8;
    const color = '#FFFFFF';

    let cx;
    if (this.scaleType === 'time') {
      cx = this.xScale(label);
    } else if (this.scaleType === 'linear') {
      cx = this.xScale(Number(label));
    } else {
      cx = this.xScale(label);
    }

    const data = {
      value,
      name: label
    };

    return {
      classNames: [`circle-data-${i}`],
      value,
      label,
      data,
      cx,
      cy,
      radius,
      height,
      tooltipLabel,
      color,
      opacity,
      gradientStops: this.getGradientStops(color),
      min: d.min,
      max: d.max
    };
  }

  getTooltipText({ tooltipLabel, value, min, max }): string {
    if (tooltipLabel === 'START' || tooltipLabel === 'END') {
      return '';
    }
    return `
      <span class="tooltip-label">
      ${tooltipLabel} : ${value.toLocaleString()}${this.getTooltipMinMaxText(min, max)}
      </span>
      <span class="tooltip-val"></span>
    `;
  }

  getTooltipMinMaxText(min: any, max: any) {
    if (min !== undefined || max !== undefined) {
      let result = ' (';
      if (min !== undefined) {
        if (max === undefined) {
          result += '≥';
        }
        result += min.toLocaleString();
        if (max !== undefined) {
          result += ' - ';
        }
      } else if (max !== undefined) {
        result += '≤';
      }
      if (max !== undefined) {
        result += max.toLocaleString();
      }
      result += ')';
      return result;
    } else {
      return '';
    }
  }

  getGradientStops(color) {
    return [
      {
        offset: 0,
        color,
        opacity: 0.2
      },
      {
        offset: 100,
        color,
        opacity: 1
      }];
  }

  onClick(value, label): void {
    if (label === 'START' || label === 'END') {
      return;
    }

    this.select.emit({
      name: label,
      value
    });
  }

  onMouseover($event, radius: any) {
    $event.target.setAttribute('r', 6);
  }

  onMouseout($event, radius: any) {
    $event.target.setAttribute('r', 6);
  }
}
